import { invokeFunction } from '../api'
import { ApiRequestError, toApiRequestError } from '../errors'
import type { ViewerContext } from '../../context/AuthContext'
import { hasAccountPermission } from './adminUsers'

export type EhrVendor = 'athena' | 'custom_fhir' | 'epic' | 'greenway' | 'modmed' | 'nextgen' | 'sis'
export type EhrAuthMode = 'oauth2_client_credentials' | 'smart_on_fhir' | 'api_key'
export type EhrConnectionStatus = 'pending' | 'verifying' | 'connected' | 'failed' | 'revoked'

export interface IntegrationStatus {
  source_system: string
  status: 'configured' | 'not_configured' | 'error'
  latest_ingestion_timestamp: string | null
  diagnostics?: Record<string, unknown>
}

export interface IntegrationRun {
  ingestion_timestamp: string
  item_count: number
}

export interface IntegrationReferenceItem {
  source_system: string
  ingestion_timestamp: string
  external_record_id: string
  record_payload: Record<string, unknown>
}

export interface EhrConnection {
  ehr_connection_id: string
  facility_id: string
  vendor: EhrVendor
  auth_mode: EhrAuthMode
  connection_status: EhrConnectionStatus
  last_verified_at: string | null
  last_error: string | null
  baa_acknowledged_at: string | null
  created_at: string | null
  updated_at: string | null
}

export interface EhrConnectInput {
  facility_id: string
  vendor: EhrVendor
  auth_mode: EhrAuthMode
  fhir_base_url: string
  oauth_client_id?: string | null
  oauth_client_secret_ref?: string | null
  api_key_ref?: string | null
  scopes?: string[]
  baa_acknowledged_at?: string | null
}

export interface EhrVerifyResult {
  ehr_connection_id: string
  status: EhrConnectionStatus
  fhir_version: string | null
  verified_at: string | null
  error: string | null
}

const VALID_EHR_VENDORS: EhrVendor[] = ['athena', 'custom_fhir', 'epic', 'greenway', 'modmed', 'nextgen', 'sis']
const VALID_EHR_AUTH_MODES: EhrAuthMode[] = ['oauth2_client_credentials', 'smart_on_fhir', 'api_key']
const VALID_EHR_CONNECTION_STATUSES: EhrConnectionStatus[] = ['pending', 'verifying', 'connected', 'failed', 'revoked']

export const EHR_VENDOR_LABELS: Record<EhrVendor, string> = {
  athena: 'athenahealth',
  custom_fhir: 'Custom FHIR',
  epic: 'Epic',
  greenway: 'Greenway',
  modmed: 'ModMed',
  nextgen: 'NextGen',
  sis: 'SIS',
}

function asRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function canTriggerIntegrationSync(viewerContext: ViewerContext | null): boolean {
  return canManageIntegrationWorkspace(viewerContext)
}

export function isIntegrationReadOnly(viewerContext: ViewerContext | null): boolean {
  return !canManageIntegrationWorkspace(viewerContext)
}

export function canManageIntegrationWorkspace(viewerContext: ViewerContext | null): boolean {
  return (
    viewerContext?.role === 'Admin' ||
    hasAccountPermission(viewerContext, [
      'delegated_account_admin',
      'manage_integrations',
    ])
  )
}

export function supportContextFromError(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const tracePart = error.traceId ? `trace=${error.traceId}` : 'trace=unavailable'
    const requestPart = error.requestId ? `request=${error.requestId}` : 'request=unavailable'
    return `${tracePart} ${requestPart}`
  }
  return 'trace=unavailable request=unavailable'
}

function toStatus(value: unknown): IntegrationStatus {
  if (!asRecord(value)) throw new Error('Malformed integration status response from server')
  if (typeof value.source_system !== 'string') throw new Error('Malformed integration status response from server')
  const rawStatus = value.status
  const status =
    rawStatus === 'configured' || rawStatus === 'not_configured' || rawStatus === 'error'
      ? rawStatus
      : 'error'

  return {
    source_system: value.source_system,
    status,
    latest_ingestion_timestamp: typeof value.latest_ingestion_timestamp === 'string' ? value.latest_ingestion_timestamp : null,
    diagnostics: asRecord(value.diagnostics) ? value.diagnostics : undefined,
  }
}

function toRuns(value: unknown): IntegrationRun[] {
  if (!Array.isArray(value)) return []
  return value
    .map((row) => {
      if (!asRecord(row)) return null
      if (typeof row.ingestion_timestamp !== 'string') return null
      if (typeof row.item_count !== 'number') return null
      return {
        ingestion_timestamp: row.ingestion_timestamp,
        item_count: row.item_count,
      }
    })
    .filter((row): row is IntegrationRun => row !== null)
}

function toReferenceItems(value: unknown): IntegrationReferenceItem[] {
  if (!Array.isArray(value)) return []
  return value
    .map((row) => {
      if (!asRecord(row)) return null
      if (typeof row.source_system !== 'string') return null
      if (typeof row.ingestion_timestamp !== 'string') return null
      if (typeof row.external_record_id !== 'string') return null
      return {
        source_system: row.source_system,
        ingestion_timestamp: row.ingestion_timestamp,
        external_record_id: row.external_record_id,
        record_payload: asRecord(row.record_payload) ? row.record_payload : {},
      }
    })
    .filter((row): row is IntegrationReferenceItem => row !== null)
}

function toEhrVendor(value: unknown): EhrVendor {
  return VALID_EHR_VENDORS.includes(value as EhrVendor) ? (value as EhrVendor) : 'custom_fhir'
}

function toEhrAuthMode(value: unknown): EhrAuthMode {
  return VALID_EHR_AUTH_MODES.includes(value as EhrAuthMode) ? (value as EhrAuthMode) : 'oauth2_client_credentials'
}

function toEhrConnectionStatus(value: unknown): EhrConnectionStatus {
  return VALID_EHR_CONNECTION_STATUSES.includes(value as EhrConnectionStatus) ? (value as EhrConnectionStatus) : 'failed'
}

function toEhrConnection(value: unknown): EhrConnection {
  if (!asRecord(value)) throw new Error('Malformed EHR connection response from server')
  if (typeof value.ehr_connection_id !== 'string') throw new Error('Malformed EHR connection response from server')
  if (typeof value.facility_id !== 'string') throw new Error('Malformed EHR connection response from server')

  return {
    ehr_connection_id: value.ehr_connection_id,
    facility_id: value.facility_id,
    vendor: toEhrVendor(value.vendor),
    auth_mode: toEhrAuthMode(value.auth_mode),
    connection_status: toEhrConnectionStatus(value.connection_status),
    last_verified_at: typeof value.last_verified_at === 'string' ? value.last_verified_at : null,
    last_error: typeof value.last_error === 'string' ? value.last_error : null,
    baa_acknowledged_at: typeof value.baa_acknowledged_at === 'string' ? value.baa_acknowledged_at : null,
    created_at: typeof value.created_at === 'string' ? value.created_at : null,
    updated_at: typeof value.updated_at === 'string' ? value.updated_at : null,
  }
}

function toEhrConnections(value: unknown): EhrConnection[] {
  if (!Array.isArray(value)) return []
  return value.map((row) => toEhrConnection(row))
}

function toEhrVerifyResult(value: unknown): EhrVerifyResult {
  if (!asRecord(value)) throw new Error('Malformed EHR verification response from server')
  if (typeof value.ehr_connection_id !== 'string') throw new Error('Malformed EHR verification response from server')

  return {
    ehr_connection_id: value.ehr_connection_id,
    status: toEhrConnectionStatus(value.status),
    fhir_version: typeof value.fhir_version === 'string' ? value.fhir_version : null,
    verified_at: typeof value.verified_at === 'string' ? value.verified_at : null,
    error: typeof value.error === 'string' ? value.error : null,
  }
}

export async function getIntegrationStatus(sourceSystem = 'external-reference'): Promise<IntegrationStatus> {
  const { data, error } = await invokeFunction<unknown>('integrations-manager', {
    method: 'GET',
    path: `/status?source_system=${encodeURIComponent(sourceSystem)}`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load integration status')
  return toStatus(data)
}

export async function getIntegrationRuns(sourceSystem = 'external-reference'): Promise<IntegrationRun[]> {
  const { data, error } = await invokeFunction<{ runs: unknown }>('integrations-manager', {
    method: 'GET',
    path: `/runs?source_system=${encodeURIComponent(sourceSystem)}`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load integration runs')
  if (!data) throw new Error('Malformed integration runs response from server')
  return toRuns(data.runs)
}

export async function getIntegrationReferenceData(sourceSystem = 'external-reference'): Promise<IntegrationReferenceItem[]> {
  const { data, error } = await invokeFunction<{ items: unknown }>('integrations-manager', {
    method: 'GET',
    path: `/reference-data?source_system=${encodeURIComponent(sourceSystem)}&limit=25`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load integration data')
  if (!data) throw new Error('Malformed integration data response from server')
  return toReferenceItems(data.items)
}

export async function runIntegrationSync(sourceSystem = 'external-reference'): Promise<void> {
  const { error } = await invokeFunction<unknown>('integrations-manager', {
    method: 'POST',
    path: '/sync',
    body: { source_system: sourceSystem },
  })
  if (error) throw toApiRequestError(error, 'Failed to run integration sync')
}

export async function getEhrConnections(facilityId?: string | null): Promise<EhrConnection[]> {
  const query = facilityId ? `?facility_id=${encodeURIComponent(facilityId)}` : ''
  const { data, error } = await invokeFunction<{ connections: unknown }>('integrations-manager', {
    method: 'GET',
    path: `/ehr-status${query}`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load EHR connection status')
  if (!data) throw new Error('Malformed EHR connection status response from server')
  return toEhrConnections(data.connections)
}

export async function connectEhr(input: EhrConnectInput): Promise<EhrConnection> {
  const { data, error } = await invokeFunction<unknown>('integrations-manager', {
    method: 'POST',
    path: '/ehr-connect',
    body: input,
  })
  if (error) throw toApiRequestError(error, 'Failed to register EHR connection')
  return toEhrConnection(data)
}

export async function verifyEhrConnection(
  ehrConnectionId: string,
  input?: { client_secret?: string; api_key?: string; token_endpoint?: string },
): Promise<EhrVerifyResult> {
  const { data, error } = await invokeFunction<unknown>('integrations-manager', {
    method: 'POST',
    path: '/ehr-verify',
    body: {
      ehr_connection_id: ehrConnectionId,
      ...input,
    },
  })
  if (error) throw toApiRequestError(error, 'Failed to verify EHR connection')
  return toEhrVerifyResult(data)
}
